import * as util from "./util.js";
import * as vectormath from "./vectormath.js";
import * as sinterface from "./interface.js";
import type { AppState } from "./app.js";
import type { UI } from "./ui.js";
import * as cconst from "./const.js";
const config = {
  NUM_TILES: 5,
  TEST_TILES: 15,
};
class Tile {
  idx: number;
  points: number[];

  constructor(idx: number) {
    this.idx = idx;
    this.points = [];
  }
}

const TileOptGenerator = class TileOptGenerator extends sinterface.MaskGenerator {
  tiles: Tile[];
  instances: { tile: Tile; tx: number; ty: number }[];
  numtiles: number;
  testtiles: number;
  celldimen: number;
  cellsize: number;
  maxgen: number;

  constructor(appstate?: AppState, dilute_small_mask?: boolean) {
    super(appstate, dilute_small_mask);

    this.tiles = [];
    this.instances = [];
    this.numtiles = 5;
    this.skip_point_draw = false;
    this.dimen = 32;
  }

  static build_ui(gui: any) {
    const panel2 = gui.panel("Tile Opt");
    panel2.slider("NUM_TILES", "Tiles", 5, 1, 15, 1, true);
    panel2.slider("TEST_TILES", "Test Tiles", 5, 1, 35, 1, true);
  }

  reset(size: number, appstate: AppState, mask_image: ImageData) {
    super.reset(size, appstate, mask_image);

    this.dimen = size;
    this.points = [];

    this.numtiles = this.config.NUM_TILES;
    this.testtiles = this.config.TEST_TILES;
    this.tiles = [];
    this.instances = [];

    for (let ti = 0; ti < this.numtiles; ti++) {
      const r = this.get_radius();
      const tile = new Tile(ti);

      this.tiles.push(tile);

      for (let i = 0; i < size * size; i++) {
        const x = util.random();
        const y = util.random();
        const pi = tile.points.length;
        for (let j = 0; j < PTOT; j++) {
          tile.points.push(0.0);
        }

        const gen = ti * size * size + i;

        tile.points[pi + PX] = x;
        tile.points[pi + PY] = y;
        tile.points[pi + PGEN] = tile.points[pi + PD] = i / (size * size);
        tile.points[pi + PR] = tile.points[pi + PR2] = r;
      }
    }

    this.r = 0.8 / (this.testtiles * this.dimen);

    console.log("Numtiles", this.tiles.length);
    this.throw();
  }

  get_radius(): number {
    return 0.8 / this.dimen;
  }

  is_done(): boolean {
    return false;
  }

  current_level(): number {
    return 10;
  }

  // (the real step() is defined below; this empty duplicate was removed)

  throw() {
    util.seed(0);

    this.points.length = 0;
    const ps = this.points;
    const r = this.get_radius();
    const dimen = this.dimen;

    let cx = (this.celldimen = this.testtiles);

    cx = Math.max(cx, 1);
    const cellsize = (this.cellsize = 1.0 / cx);

    for (let i = 0; i < cx * cx; i++) {
      const tx = i % cx;
      const ty = ~~(i / cx);
      const fx = (tx / this.numtiles) * 5.0;
      const fy = (ty / this.numtiles) * 5.0;
      const dx = Math.tent(fx);
      const dy = Math.tent(fy);
      const seed = Math.PI / 4.0;
      const dx2 = Math.cos(seed) * dx + Math.sin(seed) * dy;
      const dy2 = Math.cos(seed) * dy - Math.sin(seed) * dx;
      const f = Math.sqrt(dx2 * dx + dy2 * dy);
      const a = ~~(f * this.numtiles);
      let tt = (i / (cx * cx)) * this.numtiles * this.numtiles * 6.05;
      tt += a;
      tt = ~~tt;

      const ti = tt % this.tiles.length;
      const tile = this.tiles[ti];

      this.instances.push({
        tile,
        tx,
        ty,
      });

      const ps2 = tile.points;
      for (let pi1 = 0; pi1 < ps2.length; pi1 += PTOT) {
        const pi2 = ps.length;

        for (let k = 0; k < PTOT; k++) {
          ps.push(ps2[pi1 + k]);
        }

        ps[pi2 + PX] = tx * cellsize + ps[pi2 + PX] * cellsize;
        ps[pi2 + PY] = ty * cellsize + ps[pi2 + PY] * cellsize;

        this.find_mask_pixel(0, pi2);

        ps[pi2 + PDX] = ti;
        ps[pi2 + PDY] = pi1;
        ps[pi2 + POX] = tx * cellsize;
        ps[pi2 + POY] = ty * cellsize;
      }
    }

    this.maxgen = this.points.length;

    this.regen_spatial();
    this.raster();
  }

  step() {
    this.relax();
    this.regen_spatial();
    this.raster();
  }

  relax() {
    super.relax(...arguments);

    const cellsize = this.cellsize;

    for (const tile of this.tiles) {
      const ps = tile.points;
      for (let pi = 0; pi < ps.length; pi += PTOT) {
        ps[pi + POLDX] = 0;
      }
    }

    const ps = this.points;
    for (let pi = 0; pi < ps.length; pi += PTOT) {
      let x = ps[pi];
      let y = ps[pi + 1];

      const ti = ps[pi + PDX];
      const pi1 = ps[pi + PDY];
      const ox = ps[pi + POX];
      const oy = ps[pi + POY];
      const tile = this.tiles[ti];

      x = (x - ox) / cellsize;
      y = (y - oy) / cellsize;

      const ps2 = tile.points;
      if (ps2[pi1 + POLDX] == 0) {
        ps2[pi1 + PX] = ps2[pi1 + PY] = 0.0;
      }

      ps2[pi1 + PX] += x;
      ps2[pi1 + PY] += y;

      ps2[pi1 + POLDX] += 1.0;
    }

    for (const tile of this.tiles) {
      const ps = tile.points;
      for (let pi = 0; pi < ps.length; pi += PTOT) {
        if (ps[pi + POLDX] > 0) {
          ps[pi + PX] /= ps[pi + POLDX];
          ps[pi + PY] /= ps[pi + POLDX];
          ps[pi + PX] = Math.fract(ps[pi + PX]);
          ps[pi + PY] = Math.fract(ps[pi + PY]);
        }
      }
    }

    for (let pi = 0; pi < ps.length; pi += PTOT) {
      const x = ps[pi];
      const y = ps[pi + 1];
      const ti = ps[pi + PDX];
      const pi1 = ps[pi + PDY];
      const ox = ps[pi + POX];
      const oy = ps[pi + POY];
      const tile = this.tiles[ti];
      const ps2 = tile.points;

      ps[pi + PX] = ps2[pi1 + PX] * cellsize + ox;
      ps[pi + PY] = ps2[pi1 + PY] * cellsize + oy;
      this.find_mask_pixel(0, pi);
    }
  }

  max_level(): number {
    return 1.0;
  }

  draw(g: CanvasRenderingContext2D) {
    console.log(this.points);
    g.beginPath();
    g.rect(0, 0, 1, 1);
    g.stroke();

    const cx = this.celldimen;
    const cellsize = this.cellsize;

    for (const inst of this.instances) {
      const tile = inst.tile;
      const tx = inst.tx * cellsize;
      const ty = inst.ty * cellsize;
      const i1 = tile.idx;

      g.beginPath();
      g.rect(tx, ty, cellsize, cellsize);
      g.stroke();

      const f = Math.fract(i1 * 0.1234 * Math.sqrt(3.0));
      let r = Math.fract(f * 3.0) * 255;
      let g1 = Math.fract(f * 5.0 + 0.5) * 255;
      let b = Math.fract(f * 4.5 + 0.25) * 255;

      r = ~~r;
      g1 = ~~g1;
      b = ~~b;

      g.fillStyle = `rgba(${r},${g1},${b}, 0.5)`;
      g.fill();
    }
  }
};

sinterface.MaskGenerator.register(config, TileOptGenerator, "TILEOPT", 9);

export { config, Tile, TileOptGenerator };
